import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import Validator from 'validator'
import { OrganizationSettings } from '../dtos/OrganizationDTO'
import useCredentials from './useCredentials'

export const useOrganizationWLSettings = () => {
  const { credentials } = useCredentials()
  const params = useParams<{ id: string }>()

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<OrganizationSettings>({} as OrganizationSettings)
  const [isWhiteLabel, setIsWhiteLabel] = useState(false)
  const [subDomain, setSubDomain] = useState('')
  const [isFormEdited, setIsFormEdited] = useState(false)
  const [loadDataError, setLoadDataError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [sendgridSenderStatus, setSendgridSenderStatus] = useState<string | null>(null)
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null)

  const loadOrganization = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`/api/panel/organizations/${params.id}/edit`, {
        headers: credentials,
      })
      setFormData(res.data.draft_settings)
      setIsWhiteLabel(res.data.white_label)
      setSubDomain(res.data.subdomain)
      setSendgridSenderStatus(res.data.sendgrid_sender_status || null)
      setFaviconUrl(res.data.favicon_url || null)
    } catch (e) {
      setLoadDataError(e.message)
    } finally {
      setLoading(false)
    }
  }, [credentials, params.id])

  useEffect(() => {
    loadOrganization()
  }, [loadOrganization])

  const handleSubmit = async () => {
    const errors: any = {}
    if (formData.ContactApplication) {
      if (formData.ContactApplication.PointOfContactEmail) {
        if (!Validator.isEmail(formData.ContactApplication.PointOfContactEmail)) {
          errors.ContactApplication = {}
          errors.ContactApplication.PointOfContactEmail = 'Invalid email'
        }
      }
      if (formData.ContactApplication.DisplayContactInfo?.Email) {
        if (!Validator.isEmail(formData.ContactApplication.DisplayContactInfo.Email)) {
          if (!errors.ContactApplication) {
            errors.ContactApplication = {}
          }
          if (!errors.ContactApplication.DisplayContactInfo) {
            errors.ContactApplication.DisplayContactInfo = {}
          }
          errors.ContactApplication.DisplayContactInfo.Email = 'Invalid email'
        }
      }
      if (formData.ContactApplication.Socials) {
        Object.entries(formData.ContactApplication.Socials).forEach(([key, value]) => {
          if (value) {
            if (!Validator.isURL(value)) {
              if (!errors.ContactApplication) {
                errors.ContactApplication = {}
              }
              if (!errors.ContactApplication.Socials) {
                errors.ContactApplication.Socials = {}
              }
              errors.ContactApplication.Socials[key] = 'Invalid URL'
            }
          }
        })
      }
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return false
    }
    try {
      await axios.put(
        `/api/panel/organizations/${params.id}/update_settings`,
        { draft_settings: formData },
        {
          headers: credentials,
        }
      )
      setIsFormEdited(false)
      setValidationErrors({})
      await loadOrganization()
      return true
    } catch (e) {
      setValidationErrors(e.response?.data)
      return false
    }
  }

  return {
    formData,
    isWhiteLabel,
    subDomain,
    isFormEdited,
    loadDataError,
    validationErrors,
    handleSubmit,
    loading,
    setFormData,
    setIsFormEdited,
    organizationId: params.id,
    loadOrganization,
    sendgridSenderStatus,
    faviconUrl,
  }
}
